const CARD_WIDTH = 210
const CARD_HEIGHT = 76
const CARD_GAP = 18
const AXIS_PADDING = 110
const AXIS_EVENT_MARGIN = 60
const LANE_HEIGHT = 150
const PRINT_WIDTH = 1120
const PRINT_HEIGHT = 720
const PRINT_EVENTS_PER_PAGE = 4
const PRINT_FIT_CONTENT_EVENTS_PER_PAGE = 2
const PRINT_FIT_CONTENT_MAX_HEIGHT = 360
const BUBBLE_HORIZONTAL_PADDING = 20
const BUBBLE_VERTICAL_PADDING = 24
const BUBBLE_LINE_HEIGHT_FACTOR = 1.4
const BUBBLE_GLYPH_WIDTH_FACTOR = 0.58

export const TIMELINE_CANVAS_DEFAULTS = Object.freeze({
	axis: {kind: "calendar", label: "Timeline", color: "#4b5563", strokeWidth: 4, labelColor: "#4b5563", labelBackgroundColor: "#ffffff", labelFontSize: 13, y: 300},
	lanes: [{id: "main", label: "Events"}],
	types: [{id: "event", label: "Event", laneId: "main", styleClass: "standard"}],
	styles: {
		base: {shape: "round-rectangle", fill: "#ffffff", borderColor: "#dcdfe6", borderWidth: 2, textColor: "#303133", fontSize: 13, markerShape: "ellipse", markerColor: "#3070d0", connectorColor: "#9aa5b1"},
		selection: {borderWidthDelta: 2, shadowOpacity: 0, shadowOffsetX: 3, shadowOffsetY: 4},
		classes: {standard: {borderColor: "#3070d0", markerColor: "#3070d0"}},
	},
	bubble: {fields: ["type", "title", "time"], sections: [{source: "type"}, {source: "title"}, {source: "time"}], width: CARD_WIDTH, height: CARD_HEIGHT, paddingX: 16, paddingTop: 16, paddingBottom: 16, sectionGap: 11},
	grouping: {defaultColor: "#8b5cf6", colors: []},
	links: {types: []},
	guidance: {
		enabled: true,
		storageKey: "timelineCanvasOnboardingCompletedSteps",
		stepId: "dragAddEvent",
		desktopMessage: "Drag an event type from the left onto the canvas to add it.",
		touchMessage: "Tap an event type on the left to add it.",
		initialDelayMs: 450,
	},
})

const list = value => Array.isArray(value) ? value : []
const text = value => String(value ?? "")
const finite = value => Number.isFinite(Number(value))

export function normalizeTimelineCanvasConfig(config = {}) {
	const source = config.timelineCanvas || config
	const defaults = TIMELINE_CANVAS_DEFAULTS
	const lanes = list(source.lanes).length ? source.lanes : defaults.lanes
	const types = list(source.types).length ? source.types : defaults.types
	return {
		...source,
		axis: {...defaults.axis, ...source.axis},
		lanes: lanes.map(item => ({id: text(item.id), label: text(item.label)})),
		types: types.map(item => ({id: text(item.id), label: text(item.label), laneId: text(item.laneId || lanes[0]?.id), styleClass: text(item.styleClass)})),
		styles: {base: {...defaults.styles.base, ...source.styles?.base}, selection: {...defaults.styles.selection, ...source.styles?.selection}, classes: Object.fromEntries(Object.entries({...defaults.styles.classes, ...source.styles?.classes}).map(([id, style]) => [id, {...style}]))},
		bubble: {...defaults.bubble, ...source.bubble, fields: list(source.bubble?.fields).length ? [...source.bubble.fields] : [...defaults.bubble.fields], sections: Array.isArray(source.bubble?.sections) ? [...source.bubble.sections] : [...defaults.bubble.sections]},
		grouping: {
			defaultColor: text(source.grouping?.defaultColor || defaults.grouping.defaultColor),
			colors: list(source.grouping?.colors).filter(color => typeof color === "string" && color),
		},
		links: {types: list(source.links?.types).map(type => ({...type, id: text(type.id), label: text(type.label)})).filter(type => type.id)},
		guidance: {...defaults.guidance, ...source.guidance},
	}
}

export function normalizeTimelineCanvasPayload(payload = {}) {
	const source = payload.canvasTimeline || payload
	return {
		...source,
		events: list(source.events).map((event, index) => ({
			...event, id: text(event.id), title: text(event.title), typeId: text(event.typeId || "event"),
			timing: {...(event.timing || {kind: "unknown"})}, order: finite(event.order) ? Number(event.order) : index,
		})),
		styleClassByEventId: {...source.styleClassByEventId},
		layoutByEventId: {...source.layoutByEventId},
		groups: list(source.groups).map(group => ({id: text(group.id), title: text(group.title), color: text(group.color || "#8b5cf6"), eventIds: [...new Set(list(group.eventIds).map(text))]})),
		links: normalizeTimelineLinks(source.links, source.events),
	}
}

export function normalizeTimelineLinks(links = [], events = []) {
	const ids = new Set(list(events).map(event => text(event.id)))
	const seen = new Set()
	const seenIds = new Set()
	return list(links).filter(link => {
		const key = `${link.sourceEventId}\u0000${link.targetEventId}\u0000${link.typeId}`
		if (!link.id || !link.typeId || !ids.has(link.sourceEventId) || !ids.has(link.targetEventId) || link.sourceEventId === link.targetEventId || seen.has(key) || seenIds.has(link.id)) return false
		seen.add(key)
		seenIds.add(link.id)
		return true
	}).map(link => ({...link, id: text(link.id), sourceEventId: text(link.sourceEventId), targetEventId: text(link.targetEventId), typeId: text(link.typeId), note: text(link.note)}))
}

export function orderTimelineEventsByX(events = [], positions = {}) {
	return events.map((event, index) => {
		const value = positions[event.id]?.x
		return {event, index, x: value === null || value === undefined || !Number.isFinite(Number(value)) ? Infinity : Number(value)}
	})
		.sort((a, b) => {
			return a.x - b.x || a.index - b.index
		})
		.map(({event}, order) => ({...event, order}))
}

export function swapTimelineEventX(events = [], positions = {}, firstId, secondId) {
	const first = positions[firstId], second = positions[secondId]
	if (!first || !second || !Number.isFinite(Number(first.x)) || !Number.isFinite(Number(second.x))) return null
	const layouts = {...positions, [firstId]: {...first, x: Number(second.x)}, [secondId]: {...second, x: Number(first.x)}}
	return {events: orderTimelineEventsByX(events, layouts), layouts}
}

export function parseTimelineTime(value, axisKind = "calendar") {
	if (value === null || value === undefined || value === "") return null
	if (axisKind === "numeric") return finite(value) ? Number(value) : null
	if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null
	const parsed = Date.parse(`${value}T00:00:00Z`)
	return Number.isFinite(parsed) && new Date(parsed).toISOString().slice(0, 10) === value ? parsed : null
}

export function getTimelineTime(event, config) {
	const timing = event.timing || {}
	if (timing.kind !== "point" && timing.kind !== "range") return null
	const start = parseTimelineTime(timing.start, config.axis.kind)
	const end = timing.kind === "range" ? parseTimelineTime(timing.end, config.axis.kind) : start
	return start !== null && end !== null && end >= start ? {start, end, center: (start + end) / 2} : null
}

export function formatTimelineTime(event, config) {
	const timing = event.timing || {}
	if (timing.label) return text(timing.label)
	if (timing.kind === "range" && timing.start && timing.end) return `${timing.start} – ${timing.end}`
	if (timing.kind === "point" && timing.start) return text(timing.start)
	return timing.kind === "relative" ? "Relative order" : "Time not set"
}

export function resolveTimelineStyle(config, event, assignments = {}, state = "") {
	const type = config.types.find(item => item.id === event.typeId)
	const typeStyle = config.styles.classes[type?.styleClass] || {}
	const eventStyle = config.styles.classes[assignments[event.id]] || {}
	const style = {...config.styles.base, ...typeStyle, ...eventStyle}
	const selectedStyle = state === "selected" ? {borderWidth: Math.max(1, Number(style.borderWidth) || 1) + Math.max(0, Number(config.styles.selection?.borderWidthDelta) || 0)} : {}
	return {...style, ...selectedStyle}
}

export function formatTimelineBubble(event, config, assignments = {}) {
	const lines = config.bubble.fields.map(field => {
		const descriptor = typeof field === "string" ? {source: field} : field
		return getTimelineBubbleFieldValue(event, config, descriptor)
	}).filter(Boolean)
	const fontSize = resolveTimelineStyle(config, event, assignments).fontSize
	return fitTimelineBubbleText(lines, config, fontSize)
}

function getTimelineBubbleFieldValue(event, config, descriptor) {
	const type = config.types.find(item => item.id === event.typeId)
	const builtIn = {type: type?.label || "Event", title: event.title, time: formatTimelineTime(event, config)}
	const raw = builtIn[descriptor.source] ?? event.fields?.[descriptor.source] ?? event[descriptor.source]
	const emptyText = descriptor.emptyText ?? (descriptor.source === "title" ? "Untitled event" : "")
	const value = raw === null || raw === undefined || raw === "" ? emptyText : raw
	const formatted = descriptor.format === "number" && finite(value) ? String(Number(value)) : text(value)
	const limited = descriptor.maxLength ? formatted.slice(0, Number(descriptor.maxLength)) : formatted
	return limited ? `${text(descriptor.prefix)}${descriptor.uppercase ? limited.toUpperCase() : limited}` : ""
}

export function getTimelineCardTextWidth(config) {
	const width = Math.max(150, Number(config.bubble.width) || CARD_WIDTH)
	return Math.max(1, width - 2 * (Number(config.bubble.paddingX) || 0))
}

export function getTimelineCardLayout(event, config, assignments = {}) {
	const fontSize = Number(resolveTimelineStyle(config, event, assignments).fontSize) || 13
	const width = getTimelineCardTextWidth(config)
	let offsetY = Number(config.bubble.paddingTop) || 0
	const blocks = []
	for (const [index, section] of config.bubble.sections.entries()) {
		const descriptor = typeof section === "string" ? {source: section} : section
		if (!descriptor?.source || (descriptor.timingKinds && !descriptor.timingKinds.includes(event.timing?.kind))) continue
		const value = getTimelineBubbleFieldValue(event, config, descriptor)
		if (!value.trim()) continue
		if (blocks.length) offsetY += Number(descriptor.gapBefore ?? config.bubble.sectionGap) || 0
		const size = Math.max(10, Number(descriptor.fontSize) || fontSize + (Number(descriptor.fontSizeDelta) || 0))
		const lines = value.split("\n").flatMap(paragraph => wrapTimelineBubbleParagraph(paragraph, width, size))
		const height = Math.ceil(lines.length * size * BUBBLE_LINE_HEIGHT_FACTOR)
		const block = {key: index, source: descriptor.source, label: lines.join("\n"), fontSize: size, fontWeight: descriptor.fontWeight === "bold" ? "bold" : "normal", color: descriptor.color || "text", align: ["left", "center", "right"].includes(descriptor.align) ? descriptor.align : "left", offsetY: offsetY + height / 2}
		blocks.push(block)
		offsetY += height
	}
	return {blocks, height: Math.max(Number(config.bubble.height) || CARD_HEIGHT, offsetY + (Number(config.bubble.paddingBottom) || 0))}
}

let bubbleMeasureContext

export function getTimelineBubbleTextWidth(config) {
	return Math.max(120, (Number(config.bubble.width) || CARD_WIDTH) - BUBBLE_HORIZONTAL_PADDING)
}

function measureTimelineText(value, fontSize) {
	if (!bubbleMeasureContext && typeof OffscreenCanvas !== "undefined") bubbleMeasureContext = new OffscreenCanvas(1, 1).getContext("2d")
	if (bubbleMeasureContext) {
		bubbleMeasureContext.font = `${fontSize}px Arial`
		return bubbleMeasureContext.measureText(value).width
	}
	return Array.from(value).length * fontSize * BUBBLE_GLYPH_WIDTH_FACTOR
}

function wrapTimelineBubbleParagraph(paragraph, width, fontSize) {
	if (!paragraph) return [""]
	const wrapped = []
	let current = ""
	for (const token of paragraph.match(/\s+|\S+/g) || []) {
		const candidate = current + token
		if (measureTimelineText(candidate, fontSize) <= width) {current = candidate; continue}
		if (current.trim()) {wrapped.push(current.trimEnd()); current = token.trimStart()}
		else current = token.trimStart()
		if (measureTimelineText(current, fontSize) <= width) continue
		let piece = ""
		for (const glyph of Array.from(current)) {
			if (piece && measureTimelineText(piece + glyph, fontSize) > width) {wrapped.push(piece); piece = ""}
			piece += glyph
		}
		current = piece
	}
	wrapped.push(current.trimEnd())
	return wrapped
}

function wrapTimelineBubbleLines(lines, config, fontSize) {
	const width = getTimelineBubbleTextWidth(config) - 4
	const size = Number(fontSize) || 13
	return lines.flatMap(line => text(line).split("\n").flatMap(paragraph => wrapTimelineBubbleParagraph(paragraph, width, size)))
}

export function measureTimelineBubbleHeight(event, config, assignments = {}) {
	const minimum = Math.max(60, Number(config.bubble.height) || CARD_HEIGHT)
	if (!config.bubble.fitContent) return minimum
	if (config.bubble.presentation === "card") return Math.max(minimum, getTimelineCardLayout(event, config, assignments).height)
	const fontSize = Number(resolveTimelineStyle(config, event, assignments).fontSize) || 13
	const lineCount = formatTimelineBubble(event, config, assignments).split("\n").length
	return Math.max(minimum, Math.ceil(lineCount * fontSize * BUBBLE_LINE_HEIGHT_FACTOR + BUBBLE_VERTICAL_PADDING))
}

export function fitTimelineBubbleText(lines, config, fontSize) {
	if (config.bubble.fitContent) return wrapTimelineBubbleLines(lines, config, fontSize).join("\n")
	const maxLines = Math.max(2, Math.floor((Number(config.bubble.height) - 12) / ((Number(config.styles.base.fontSize) || 13) * 1.25)))
	const maxChars = Math.max(12, Math.floor((Number(config.bubble.width) - 22) / ((Number(config.styles.base.fontSize) || 13) * 0.57)))
	const visible = lines.slice(0, maxLines).map(line => text(line).replace(/\s+/g, " ").slice(0, maxChars + 1))
	return visible.map((line, index) => {
		const clipped = line.length > maxChars || (index === visible.length - 1 && lines.length > maxLines)
		return clipped ? `${line.slice(0, maxChars - 1)}…` : line
	}).join("\n")
}

export function createTimelineProjection(payloadInput, configInput, options = {}) {
	const config = normalizeTimelineCanvasConfig(configInput)
	const payload = normalizeTimelineCanvasPayload(payloadInput)
	const width = Math.max(Number(options.width) || 800, 480)
	const bubbleWidth = Math.max(150, Number(config.bubble.width) || CARD_WIDTH)
	const bubbleHeight = Math.max(60, Number(config.bubble.height) || CARD_HEIGHT, ...payload.events.map(event => measureTimelineBubbleHeight(event, config, payload.styleClassByEventId)))
	const axisY = Math.max(180, Number(config.axis.y) || 300)
	const items = payload.events.map((event, index) => {
		const type = config.types.find(item => item.id === event.typeId)
		const time = getTimelineTime(event, config)
		return {event, time, x: AXIS_PADDING + index * (bubbleWidth + CARD_GAP), laneId: time ? (type?.laneId || config.lanes[0].id) : "untimed"}
	})
	const timed = items.filter(item => item.time).map(({event, time}) => ({event, time}))
	const untimed = items.filter(item => !item.time).map(({event}) => ({event}))
	const display = items.map(item => ({id: `event:${item.event.id}`, items: [item], x: item.x, laneId: item.laneId, typeId: item.event.typeId, bubbleHeight: measureTimelineBubbleHeight(item.event, config, payload.styleClassByEventId)}))
	const allDisplay = display
	const eventXs = allDisplay.map(item => {
		const placement = payload.layoutByEventId[item.items[0].event.id] || {}
		return placement.x !== null && placement.x !== undefined && Number.isFinite(Number(placement.x)) ? Number(placement.x) : item.x
	})
	const axisLeft = Math.min(AXIS_PADDING, ...eventXs.map(x => x - AXIS_EVENT_MARGIN))
	const axisRight = Math.max(width - AXIS_PADDING, ...eventXs.map(x => x + AXIS_EVENT_MARGIN))
	const worldWidth = axisRight - axisLeft + AXIS_PADDING * 2
	const occupied = new Map()
	for (const [index, item] of allDisplay.entries()) {
		const placement = payload.layoutByEventId[item.items[0].event.id] || {}
		if (placement.x !== null && placement.x !== undefined && Number.isFinite(Number(placement.x))) item.x = Number(placement.x)
		item.isManual = placement.y !== null && placement.y !== undefined && Number.isFinite(Number(placement.y))
		const overlapsAbove = allDisplay.slice(0, index).some(other => !other.isManual && other.side === "above" && Math.abs(other.x - item.x) < bubbleWidth + CARD_GAP)
		const side = placement.side === "below" ? "below" : placement.side === "above" ? "above" : item.laneId === "untimed" || overlapsAbove ? "below" : "above"
		item.side = side
		if (item.isManual) {item.row = 0; continue}
		const key = `${item.laneId}:${side}`
		const positions = occupied.get(key) || []
		let row = 0
		while (positions.some(other => other.row === row && Math.abs(other.x - item.x) < bubbleWidth + CARD_GAP)) row++
		positions.push({x: item.x, row})
		occupied.set(key, positions)
		item.row = row
	}
	const laneBase = new Map()
	for (const side of ["above", "below"]) {
		let distance = 100
		for (const laneId of [...config.lanes.map(lane => lane.id), "untimed"]) {
			laneBase.set(`${laneId}:${side}`, distance)
			const rows = occupied.get(`${laneId}:${side}`) || []
			const rowCount = Math.max(1, ...rows.map(item => item.row + 1))
			distance += Math.max(LANE_HEIGHT, rowCount * (bubbleHeight + CARD_GAP) + CARD_GAP)
		}
	}
	for (const item of allDisplay) {
		const placement = payload.layoutByEventId[item.items[0].event.id] || {}
		item.distance = Number.isFinite(Number(placement.distance)) && Number(placement.distance) >= 0
			? Number(placement.distance)
			: (laneBase.get(`${item.laneId}:${item.side}`) || 100) + item.row * (bubbleHeight + CARD_GAP)
	}
	const belowDistance = Math.max(100, ...allDisplay.filter(item => item.side === "below").map(item => item.distance))
	for (const item of allDisplay) {
		const placement = payload.layoutByEventId[item.items[0].event.id] || {}
		item.y = placement.y !== null && placement.y !== undefined && Number.isFinite(Number(placement.y))
			? Number(placement.y) : axisY + (item.side === "above" ? -item.distance : item.distance)
	}
	const laneY = new Map(config.lanes.map(lane => [lane.id, axisY - (laneBase.get(`${lane.id}:above`) || 100)]))
	const height = Math.max(axisY + belowDistance, ...allDisplay.map(item => item.y)) + bubbleHeight / 2 + 70
	return {config, payload, timed, untimed, display: allDisplay, width: worldWidth, height, axisY, axisLeft, axisRight, laneY, bubbleWidth, bubbleHeight}
}

export function materializeTimelineLayout(payloadInput, configInput, currentLayouts = {}) {
	const payload = normalizeTimelineCanvasPayload(payloadInput)
	const layoutByEventId = Object.fromEntries(payload.events.map(event => [event.id, {...currentLayouts[event.id], ...payload.layoutByEventId[event.id]}]))
	const projection = createTimelineProjection({...payload, layoutByEventId}, configInput)
	return Object.fromEntries(projection.display.map(item => {
		const eventId = item.items[0].event.id
		return [eventId, {x: item.x, y: item.y, side: item.side, distance: Math.abs(item.y - projection.axisY)}]
	}))
}

export function createTimelinePrintPages(payloadInput, configInput) {
	const config = normalizeTimelineCanvasConfig(configInput)
	const payload = normalizeTimelineCanvasPayload(payloadInput)
	const pages = []
	for (const lane of config.lanes) {
		const ordered = payload.events.filter(event => (config.types.find(type => type.id === event.typeId)?.laneId || config.lanes[0].id) === lane.id)
		for (let index = 0; index < ordered.length;) {
			const next = ordered.slice(index, index + (config.bubble.fitContent ? PRINT_FIT_CONTENT_EVENTS_PER_PAGE : PRINT_EVENTS_PER_PAGE))
			const events = config.bubble.fitContent && next.some(event => measureTimelineBubbleHeight(event, config, payload.styleClassByEventId) > PRINT_FIT_CONTENT_MAX_HEIGHT) ? next.slice(0, 1) : next
			pages.push({events, lane, number: pages.length + 1, width: PRINT_WIDTH, height: PRINT_HEIGHT})
			index += events.length
		}
	}
	if (!pages.length) pages.push({events: [], lane: config.lanes[0], number: 1, width: PRINT_WIDTH, height: PRINT_HEIGHT})
	return pages.map(page => ({...page, total: pages.length}))
}
